import { invalidate, ThreeEvent, useThree } from '@react-three/fiber';
import { type RefObject, useCallback, useEffect, useMemo } from 'react';
import { type BatchedMesh } from 'three';

import {
    getClickCount,
    isInputActionBound,
    matchesInputAction
} from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    DOUBLE_CLICK_MS,
    chordFromMouseEvent
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { updateAttributeRange } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePoiSelectionInfo } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiSelectionInfo';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isCornerSingleSidePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { isSceneToolActive } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';

const updatedIndices = new Set<number>();
let leaveNavigationTimer: ReturnType<typeof setTimeout> | undefined;

interface PoiEventHandlerProps {
    entries: PoiBatchedEntry[];
    meshRef: RefObject<BatchedMesh | null>;
    hoveredBatchIdRef: RefObject<number | null>;
    instanceIdByEntryIndexRef: RefObject<number[]>;
    entryIndexByInstanceIdRef: RefObject<Map<number, number>>;
}

export const usePoiEventHandler = ({
    entries,
    meshRef,
    hoveredBatchIdRef,
    instanceIdByEntryIndexRef,
    entryIndexByInstanceIdRef
}: PoiEventHandlerProps) => {
    const { prevFilterTypeRef, prevIndexRef, selectedPoiIndexRef } = usePoiSelectionInfo();

    const gl = useThree((state) => state.gl);
    useEffect(
        () => () => {
            if (gl.domElement.style.cursor === 'pointer' && !isSceneToolActive(usePlacementStore.getState())) {
                gl.domElement.style.removeProperty('cursor');
            }
        },
        [gl]
    );
    const entryIndexByPoiIndex = useMemo(
        () => new Map(entries.map((entry, entryIndex) => [entry.poiIndex, entryIndex])),
        [entries]
    );

    const resolveBatchEvent = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            const batchId = event.batchId;
            if (batchId === undefined) {
                return null;
            }

            const entryIndex = entryIndexByInstanceIdRef.current.get(batchId) ?? batchId;
            const entry = entries[entryIndex];
            if (!entry) {
                return null;
            }

            return { batchId, entry, entryIndex };
        },
        [entries, entryIndexByInstanceIdRef]
    );

    const handleSelection = useCallback(
        (
            batchId: number,
            entry: PoiBatchedEntry,
            mesh: BatchedMesh,
            previousPoiIndex: number | undefined,
            shouldRealignCamera: boolean,
            awaitsDoubleClick: boolean
        ) => {
            usePlacementStore
                .getState()
                .setDisableCameraAlign(PoiNavigationSource.PointerSelection, !shouldRealignCamera);

            const state = usePlacementStore.getState();

            if (shouldRealignCamera && entry.filterType === POIFilterType.Wall) {
                if (
                    state.mainCameraControlsRef.current &&
                    state.mainWebGLRendererRef.current &&
                    state.faceSelectionRef.current
                ) {
                    state.setFocusOnWall(
                        state.mainCameraControlsRef,
                        state.faceSelectionRef.current!,
                        state.mainWebGLRendererRef.current!.domElement
                    );
                }
            } else if (shouldRealignCamera && entry.filterType === POIFilterType.Corner) {
                const cornerPoi = state.pois[entry.poiIndex]?.poi;
                if (cornerPoi && isCornerSingleSidePoi(cornerPoi)) {
                    state.setFocusOnCorner(
                        state.mainCameraControlsRef,
                        cornerPoi,
                        state.mainWebGLRendererRef.current?.domElement ?? null
                    );
                }
            }

            state.setPoiSelection(entry.filterType, entry.poiIndex, PoiNavigationSource.PointerSelection);

            prevFilterTypeRef.current = entry.filterType;
            prevIndexRef.current = entry.poiIndex;
            updatedIndices.clear();

            const previousEntryIndex = entryIndexByPoiIndex.get(previousPoiIndex ?? -1);
            const previousBatchId =
                previousEntryIndex !== undefined ? instanceIdByEntryIndexRef.current[previousEntryIndex] : undefined;
            if (previousBatchId !== undefined && previousBatchId !== batchId) {
                const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
                if (previousEntry) {
                    mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                    updatedIndices.add(previousBatchId);
                }
            }

            mesh.setColorAt(batchId, entry.selectedColor);
            updatedIndices.add(batchId);
            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);

            hoveredBatchIdRef.current = batchId;
            invalidate();

            // Stay in navigation long enough for a pending double-click to reach the POI.
            clearTimeout(leaveNavigationTimer);
            leaveNavigationTimer = setTimeout(
                () => {
                    if (!usePlacementStore.getState().isSpaceKeyPressed) {
                        usePlacementStore.getState().setInteractionMode(InteractionMode.DEFAULT);
                    }
                },
                awaitsDoubleClick ? DOUBLE_CLICK_MS : 0
            );
        },
        [entries, entryIndexByPoiIndex, hoveredBatchIdRef, instanceIdByEntryIndexRef, prevFilterTypeRef, prevIndexRef]
    );

    const handleOnPoiSelection = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            const resolved = resolveBatchEvent(event);
            const mesh = meshRef.current;
            if (!resolved || !mesh) {
                return;
            }

            const { batchId, entry } = resolved;
            const trigger = getClickCount(event.nativeEvent) === 2 ? InputTrigger.DoubleClick : InputTrigger.Click;
            const chord = chordFromMouseEvent(event.nativeEvent, trigger);
            const shouldRealignCamera = matchesInputAction(InputActionId.PoiSelectAndAlign, chord);
            if (!shouldRealignCamera && !matchesInputAction(InputActionId.PoiSelect, chord)) {
                return;
            }

            const awaitsDoubleClick =
                trigger === InputTrigger.Click &&
                isInputActionBound(
                    InputActionId.PoiSelectAndAlign,
                    chordFromMouseEvent(event.nativeEvent, InputTrigger.DoubleClick)
                );
            handleSelection(batchId, entry, mesh, prevIndexRef.current, shouldRealignCamera, awaitsDoubleClick);

            event.stopPropagation();
        },
        [handleSelection, meshRef, prevIndexRef, resolveBatchEvent]
    );

    const handleOnPointerMove = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            if (event.buttons !== 0) {
                return;
            }

            const resolved = resolveBatchEvent(event);
            const mesh = meshRef.current;
            if (!resolved || !mesh) {
                return;
            }

            const { batchId, entry } = resolved;
            event.stopPropagation();
            if (hoveredBatchIdRef.current === batchId) {
                return;
            }

            gl.domElement.style.setProperty('cursor', 'pointer');
            updatedIndices.clear();

            const previousBatchId = hoveredBatchIdRef.current;
            if (previousBatchId !== null) {
                const previousEntryIndex = entryIndexByInstanceIdRef.current.get(previousBatchId);
                const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
                if (previousEntry && selectedPoiIndexRef.current !== previousEntry.poiIndex) {
                    mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                    updatedIndices.add(previousBatchId);
                }
            }

            if (selectedPoiIndexRef.current !== entry.poiIndex) {
                mesh.setColorAt(batchId, entry.hoverColor ?? entry.selectedColor);
                updatedIndices.add(batchId);
            }

            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
            hoveredBatchIdRef.current = batchId;
            invalidate();
        },
        [
            entries,
            entryIndexByInstanceIdRef,
            gl.domElement.style,
            hoveredBatchIdRef,
            meshRef,
            resolveBatchEvent,
            selectedPoiIndexRef
        ]
    );

    const handleOnPointerLeave = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            const isToolActive = isSceneToolActive(usePlacementStore.getState());
            if (event.buttons !== 0 && !isToolActive) {
                return;
            }

            if (!isToolActive) {
                gl.domElement.style.setProperty('cursor', 'auto');
            }

            const previousBatchId = hoveredBatchIdRef.current;
            if (previousBatchId === null) {
                return;
            }

            const previousEntryIndex = entryIndexByInstanceIdRef.current.get(previousBatchId);
            const previousEntry = previousEntryIndex !== undefined ? entries[previousEntryIndex] : undefined;
            const mesh = meshRef.current;
            if (!mesh || !previousEntry) {
                return;
            }

            updatedIndices.clear();

            if (selectedPoiIndexRef.current !== previousEntry.poiIndex) {
                mesh.setColorAt(previousBatchId, previousEntry.defaultColor);
                updatedIndices.add(previousBatchId);
            }

            updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
            hoveredBatchIdRef.current = null;
            invalidate();
        },
        [entries, entryIndexByInstanceIdRef, gl.domElement.style, hoveredBatchIdRef, meshRef, selectedPoiIndexRef]
    );

    return {
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave
    };
};
